import * as XLSX from 'xlsx-js-style';

/*
 * The Go Colors candidate sheet that ships inside the ZIP export.
 *
 * Column order and the exact header spellings are a contract with the client's
 * onboarding/payroll team - they paste this sheet into their own system, so a
 * renamed or reordered column silently breaks them. Everything else in here
 * (fills, widths, borders) is cosmetic and exists to match the formatted
 * template they sent us.
 *
 * Built with xlsx-js-style rather than plain `xlsx`: the community SheetJS build
 * drops the `s` cell property on write, so every style below would be lost.
 */

const SHEET_NAME = 'Candidates';

const HEADER_FILL = 'FFF2CC';
const HEADER_FONT_COLOR = '3F3F3F';
const HEADER_BORDER_COLOR = 'BFBFBF';
const DATA_BORDER_COLOR = 'D9D9D9';
const LINK_FONT_COLOR = '0563C1';
const FONT_NAME = 'Calibri';
const FONT_SIZE = 11;

const HEADER_ROW_HEIGHT = 30;
const DATA_ROW_HEIGHT = 18;
const WRAPPED_LINE_HEIGHT = 14;
const MAX_WRAPPED_LINES = 4;
const MAX_AUTOFIT_WIDTH = 45;
const AUTOFIT_PADDING = 2;

// `forceText` keeps Excel from mangling long digit strings - a 12-digit Aadhaar or
// UAN landing in a General cell renders as 5.34535E+11, and a leading zero on a
// mobile number disappears outright.
export const GO_COLORS_COLUMNS = [
  { key: 'storeCode', header: 'StoreCode', width: 12, align: 'center', forceText: true },
  { key: 'aadhaar', header: 'Aadhaar', width: 16, forceText: true },
  { key: 'name', header: 'Name', width: 22 },
  { key: 'dob', header: 'DOB', width: 12, align: 'center' },
  { key: 'gender', header: 'Gender', width: 10, align: 'center' },
  { key: 'maritalStatus', header: 'MaritalStatus', width: 14, align: 'center' },
  { key: 'mobileNumber', header: 'MobileNumber', width: 15, align: 'center', forceText: true },
  { key: 'emailId', header: 'EmailId', width: 26, kind: 'email' },
  { key: 'presentAddress', header: 'PresentAddress', width: 30, wrap: true },
  { key: 'permanentAddress', header: 'PermanentAddress', width: 30, wrap: true },
  { key: 'designation', header: 'Designation', width: 20 },
  { key: 'doj', header: 'DOJ', width: 12, align: 'center' },
  { key: 'grossSalary', header: 'GrossSalary', width: 14, align: 'right', kind: 'number', numFmt: '#,##0' },
  { key: 'pan', header: 'PAN', width: 14, align: 'center', forceText: true },
  { key: 'panAcknowledgement', header: 'PANAcknowledgement', width: 22, forceText: true },
  { key: 'esic', header: 'ESIC', width: 16, forceText: true },
  { key: 'uan', header: 'UAN', width: 16, forceText: true },
  { key: 'bankAccountNumber', header: 'Bank A/C', width: 20, forceText: true },
  { key: 'bankIfsc', header: 'Bank IFSC Code', width: 14, align: 'center', forceText: true },
  { key: 'bankHolderName', header: 'Name as per Bank', width: 22 },
  { key: 'bankName', header: 'Bank Name', width: 22 },
  // Appended, never inserted: the columns above are pasted by position.
  { key: 'documents', header: 'Documents Submitted', width: 12, align: 'center', forceText: true },
  { key: 'selectedDate', header: 'Selected Date', width: 14, align: 'center' },
];

export const GO_COLORS_EXCEL_HEADERS = GO_COLORS_COLUMNS.map((column) => column.header);

const thinBorder = (rgb) => ({
  top: { style: 'thin', color: { rgb } },
  bottom: { style: 'thin', color: { rgb } },
  left: { style: 'thin', color: { rgb } },
  right: { style: 'thin', color: { rgb } },
});

const HEADER_BORDER = thinBorder(HEADER_BORDER_COLOR);
const DATA_BORDER = thinBorder(DATA_BORDER_COLOR);

function toText(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  return String(value).trim();
}

// Only a bare amount becomes a numeric cell; "2.5 LPA" or "As per policy" stays text
// so nothing the recruiter typed is lost to a parse.
function toAmount(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = toText(value);
  if (!text || !/^(?:₹|Rs\.?|INR)?\s*\d[\d,]*(?:\.\d+)?$/i.test(text)) return null;
  const amount = Number(text.replace(/[^\d.]/g, ''));
  return Number.isFinite(amount) ? amount : null;
}

function headerCell(text) {
  return {
    v: text,
    t: 's',
    s: {
      fill: { patternType: 'solid', fgColor: { rgb: HEADER_FILL } },
      font: { name: FONT_NAME, sz: FONT_SIZE, bold: true, color: { rgb: HEADER_FONT_COLOR } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: HEADER_BORDER,
    },
  };
}

function dataCell(column, rawValue) {
  const style = {
    font: { name: FONT_NAME, sz: FONT_SIZE, color: { rgb: '000000' } },
    alignment: {
      horizontal: column.align || 'left',
      vertical: column.wrap ? 'top' : 'center',
      wrapText: Boolean(column.wrap),
    },
    border: DATA_BORDER,
  };

  if (column.kind === 'number') {
    const amount = toAmount(rawValue);
    if (amount !== null) {
      return { v: amount, t: 'n', z: column.numFmt, s: { ...style, numFmt: column.numFmt } };
    }
    return {
      v: toText(rawValue),
      t: 's',
      s: { ...style, alignment: { ...style.alignment, horizontal: 'left' } },
    };
  }

  const text = toText(rawValue);

  if (column.kind === 'email' && text) {
    return {
      v: text,
      t: 's',
      l: { Target: `mailto:${text}`, Tooltip: text },
      s: { ...style, font: { ...style.font, color: { rgb: LINK_FONT_COLOR }, underline: true } },
    };
  }

  if (column.forceText) {
    return { v: text, t: 's', z: '@', s: { ...style, numFmt: '@' } };
  }

  return { v: text, t: 's', s: style };
}

function resolveColumnWidths(rows) {
  return GO_COLORS_COLUMNS.map((column) => {
    // Wrapped columns hold their declared width - growing them defeats the wrap.
    if (column.wrap) return { wch: column.width };
    const longest = rows.reduce(
      (max, row) => Math.max(max, toText(row?.[column.key]).length),
      column.header.length
    );
    return { wch: Math.min(MAX_AUTOFIT_WIDTH, Math.max(column.width, longest + AUTOFIT_PADDING)) };
  });
}

function resolveRowHeights(rows, widths) {
  const wrapColumns = GO_COLORS_COLUMNS.map((column, index) => ({ column, index })).filter(
    ({ column }) => column.wrap
  );

  return [
    { hpt: HEADER_ROW_HEIGHT },
    ...rows.map((row) => {
      const lines = wrapColumns.reduce((max, { column, index }) => {
        const width = widths[index]?.wch || column.width;
        return Math.max(max, Math.ceil(toText(row?.[column.key]).length / width) || 1);
      }, 1);
      const capped = Math.min(lines, MAX_WRAPPED_LINES);
      return { hpt: capped > 1 ? capped * WRAPPED_LINE_HEIGHT : DATA_ROW_HEIGHT };
    }),
  ];
}

/**
 * Build the styled worksheet. `rows` are plain objects keyed by GO_COLORS_COLUMNS[].key;
 * a missing key renders as an empty - but still bordered - cell.
 */
export function buildGoColorsCandidateSheet(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const ws = {};

  GO_COLORS_COLUMNS.forEach((column, c) => {
    ws[XLSX.utils.encode_cell({ r: 0, c })] = headerCell(column.header);
  });

  safeRows.forEach((row, rowIndex) => {
    GO_COLORS_COLUMNS.forEach((column, c) => {
      ws[XLSX.utils.encode_cell({ r: rowIndex + 1, c })] = dataCell(column, row?.[column.key]);
    });
  });

  const ref = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: safeRows.length, c: GO_COLORS_COLUMNS.length - 1 },
  });

  const widths = resolveColumnWidths(safeRows);
  ws['!ref'] = ref;
  ws['!cols'] = widths;
  ws['!rows'] = resolveRowHeights(safeRows, widths);

  return ws;
}

/**
 * The styled .xlsx as an ArrayBuffer, ready to hand to JSZip.
 */
export function buildGoColorsWorkbookArray(rows) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildGoColorsCandidateSheet(rows), SHEET_NAME);
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
}
